import React from 'react';

import { Button, ButtonProps, CircularProgress, useTheme } from '@material-ui/core';

export interface AppButtonProps extends ButtonProps {
  loading?: boolean;
  loaderVariant?: 'primary' | 'paper';
  loaderSize?: number;
}

/**
 * Button with a built-in loading state. While loading, the whole label (icon + text) is kept in the
 * layout but made invisible — so the button keeps its size and doesn't shrink — and a single spinner
 * is centred over the entire button. Pass the icon via `startIcon` as usual; it's hidden while loading.
 */
export const AppButton = React.forwardRef<HTMLButtonElement, AppButtonProps>(
  (
    { loading = false, loaderVariant = 'primary', loaderSize = 22, startIcon, children, disabled, style, ...rest },
    ref
  ) => {
    const theme = useTheme();
    const loaderColor =
      loaderVariant === 'paper' ? theme.palette.background.paper : theme.palette.primary.main;

    return (
      <Button
        ref={ref}
        {...rest}
        disabled={disabled || loading}
        style={{ position: 'relative', ...style }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            visibility: loading ? 'hidden' : 'visible',
          }}>
          {startIcon}
          {children}
        </span>
        {loading && (
          // Full-button overlay that flex-centres the spinner. We must NOT centre the CircularProgress
          // itself with `transform: translate(...)` — its indeterminate rotate animation sets `transform`
          // on the same element and overrides ours, which pushes the spinner off-centre.
          <span
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <CircularProgress size={loaderSize} style={{ color: loaderColor }} />
          </span>
        )}
      </Button>
    );
  }
);
